/**
 * Company settings helpers: payment-terms date math (shared by the invoice
 * form and the mock client) and the Settings > Company form conversions.
 * Rules mirror backend company-settings.business.ts.
 */

import * as types from '../api/types';
import { percentToRate, rateToPercentString } from './taxRate';

/** Backend default when a tenant has no settings row. */
export const DEFAULT_PAYMENT_TERMS_DAYS = 30;
export const MAX_SETTINGS_DAYS = 365;

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * "2026-01-31" + 30 -> "2026-03-02". UTC calendar arithmetic (like the
 * backend's default due date), so no time zone or DST shift. Returns null for
 * anything that is not a real YYYY-MM-DD date.
 */
export function addDaysToDateOnly(date: string, days: number): string | null {
  if (!DATE_ONLY_PATTERN.test(date) || !Number.isInteger(days)) return null;
  const parsed = new Date(`${date}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) return null;
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}

/** Text fields on the Company form, with the backend's max lengths. */
export const COMPANY_TEXT_FIELDS = {
  companyName: 200,
  taxId: 50,
  logoUrl: 500,
  addressLine1: 200,
  addressLine2: 200,
  city: 100,
  state: 100,
  postalCode: 20,
  phone: 50,
  email: 254,
  website: 500,
  invoiceFooterNote: 2000,
} as const;

export type CompanyTextField = keyof typeof COMPANY_TEXT_FIELDS;

/** Form state: every field as the string the input holds. */
export type CompanySettingsForm = Record<CompanyTextField, string> & {
  defaultPaymentTermsDays: string;
  /** Percent, e.g. "1.5" for a stored "0.0150" */
  financeChargePercent: string;
  financeChargeGraceDays: string;
  financeChargeMinAmount: string;
  /** MANUAL or AUTOMATIC (a select, so always valid) */
  flatContractBillingMode: string;
  /** OFF, ON_COMPLETION or NIGHTLY (radio buttons) */
  autoInvoicingMode: string;
  /** YYYY-MM-DD or '' (blank while on = today, set by the server) */
  autoInvoicingStartDate: string;
  /** 'true' or 'false' (a checkbox) */
  autoInvoicingAutoSend: string;
  /** Hour 0-23 (UTC) */
  autoInvoicingNightlyHour: string;
  /** Dispatch (tracker item 40): IANA timezone name */
  timezone: string;
  /** Fallback job duration, minutes (5-1440) */
  defaultJobDurationMinutes: string;
  /** Working days as "1,2,3,4,5" (0 = Sunday) */
  workDays: string;
  /** "HH:MM" */
  workdayStart: string;
  workdayEnd: string;
  /** Conversation inbox (tracker item 56): STAFF or EVERYONE (radio buttons) */
  inboxAccess: string;
  /** Routing (tracker item 41): average speed in the form's distance unit (mph or km/h) */
  routingSpeed: string;
  /** Road distance factor, "1.30" */
  routingCircuityFactor: string;
  /** Minutes, 0-120 */
  routingUnknownTravelMinutes: string;
  /** MI or KM */
  distanceUnit: string;
};

/** km per mile. */
export const KM_PER_MILE = 1.609344;
export const MIN_ROUTING_SPEED_KPH = 5;
export const MAX_ROUTING_SPEED_KPH = 130;

/** A stored km/h speed in the display unit, rounded to a whole number ("30" mph for 48 km/h). */
export function speedForDisplay(kph: number, unit: string): string {
  return String(Math.round(unit === 'MI' ? kph / KM_PER_MILE : kph));
}

/** A typed speed in the display unit as whole km/h, or null when it is not a number. */
export function speedToKph(text: string, unit: string): number | null {
  const trimmed = text.trim();
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(trimmed)) return null;
  return Math.round(unit === 'MI' ? Number(trimmed) * KM_PER_MILE : Number(trimmed));
}

/** The speed field converted when the distance unit changes (a blank or invalid value is kept as typed). */
export function convertSpeedText(text: string, from: string, to: string): string {
  if (from === to) return text;
  const kph = speedToKph(text, from);
  return kph === null ? text : speedForDisplay(kph, to);
}

/** "1,2,3,4,5" <-> [1, 2, 3, 4, 5] for the working-days checkboxes. */
export function workDaysToList(value: string): number[] {
  return value.split(',').filter((p) => p !== '').map(Number).filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);
}

export function workDaysToText(days: number[]): string {
  return Array.from(new Set(days)).sort((a, b) => a - b).join(',');
}

export function toCompanySettingsForm(settings: types.CompanySettings): CompanySettingsForm {
  return {
    companyName: settings.companyName,
    taxId: settings.taxId ?? '',
    logoUrl: settings.logoUrl ?? '',
    addressLine1: settings.addressLine1 ?? '',
    addressLine2: settings.addressLine2 ?? '',
    city: settings.city ?? '',
    state: settings.state ?? '',
    postalCode: settings.postalCode ?? '',
    phone: settings.phone ?? '',
    email: settings.email ?? '',
    website: settings.website ?? '',
    invoiceFooterNote: settings.invoiceFooterNote ?? '',
    defaultPaymentTermsDays: String(settings.defaultPaymentTermsDays),
    financeChargePercent: rateToPercentString(settings.financeChargeRate),
    financeChargeGraceDays: String(settings.financeChargeGraceDays),
    financeChargeMinAmount: settings.financeChargeMinAmount,
    flatContractBillingMode: settings.flatContractBillingMode ?? 'MANUAL',
    autoInvoicingMode: settings.autoInvoicingMode ?? 'OFF',
    autoInvoicingStartDate: settings.autoInvoicingStartDate ?? '',
    autoInvoicingAutoSend: settings.autoInvoicingAutoSend ? 'true' : 'false',
    autoInvoicingNightlyHour: String(settings.autoInvoicingNightlyHour ?? 2),
    timezone: settings.timezone ?? 'America/New_York',
    defaultJobDurationMinutes: String(settings.defaultJobDurationMinutes ?? 60),
    workDays: workDaysToText(settings.workDays ?? [1, 2, 3, 4, 5]),
    workdayStart: settings.workdayStart ?? '08:00',
    workdayEnd: settings.workdayEnd ?? '17:00',
    inboxAccess: settings.inboxAccess ?? 'STAFF',
    routingSpeed: speedForDisplay(settings.routingSpeedKph ?? 48, settings.distanceUnit ?? 'MI'),
    routingCircuityFactor: settings.routingCircuityFactor ?? '1.30',
    routingUnknownTravelMinutes: String(settings.routingUnknownTravelMinutes ?? 15),
    distanceUnit: settings.distanceUnit ?? 'MI',
  };
}

const DAYS_PATTERN = /^\d{1,3}$/;
const MONEY_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export type CompanySettingsFormErrors = Partial<Record<keyof CompanySettingsForm, string>>;

/**
 * Validate the form and build the PUT body. Every field is sent (blank text
 * as null), so the request replaces the whole document. Returns the field
 * errors instead when any field is invalid.
 */
export function buildCompanySettingsRequest(
  form: CompanySettingsForm
): { request: types.UpdateCompanySettingsRequest } | { errors: CompanySettingsFormErrors } {
  const errors: CompanySettingsFormErrors = {};
  const text: Partial<Record<CompanyTextField, string | null>> = {};

  for (const field of Object.keys(COMPANY_TEXT_FIELDS) as CompanyTextField[]) {
    const value = form[field].trim();
    if (value.length > COMPANY_TEXT_FIELDS[field]) {
      errors[field] = `Must be ${COMPANY_TEXT_FIELDS[field]} characters or less`;
    }
    text[field] = value.length > 0 ? value : null;
  }
  if (!text.companyName) errors.companyName = 'Company name is required';
  for (const field of ['logoUrl', 'website'] as const) {
    if (text[field] && !isHttpUrl(text[field]!)) errors[field] = 'Enter a full http:// or https:// URL';
  }
  if (text.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text.email)) errors.email = 'Enter a valid email address';

  const days = (value: string) => {
    const trimmed = value.trim();
    return DAYS_PATTERN.test(trimmed) && Number(trimmed) <= MAX_SETTINGS_DAYS ? Number(trimmed) : null;
  };
  const terms = days(form.defaultPaymentTermsDays);
  if (terms === null) errors.defaultPaymentTermsDays = `Enter a whole number of days from 0 to ${MAX_SETTINGS_DAYS}`;
  const graceDays = days(form.financeChargeGraceDays);
  if (graceDays === null) errors.financeChargeGraceDays = `Enter a whole number of days from 0 to ${MAX_SETTINGS_DAYS}`;
  const rate = percentToRate(form.financeChargePercent);
  if (rate === null) errors.financeChargePercent = 'Enter a percentage from 0 to 100 with at most 2 decimal places';
  const minAmount = form.financeChargeMinAmount.trim();
  if (!MONEY_PATTERN.test(minAmount)) errors.financeChargeMinAmount = 'Enter an amount of 0 or more with at most 2 decimal places';
  const mode = form.flatContractBillingMode;
  if (!(types.FLAT_CONTRACT_BILLING_MODES as readonly string[]).includes(mode)) {
    errors.flatContractBillingMode = 'Choose manual or automatic billing';
  }

  const autoMode = form.autoInvoicingMode;
  if (!(types.AUTO_INVOICING_MODES as readonly string[]).includes(autoMode)) {
    errors.autoInvoicingMode = 'Choose when invoices are created';
  }
  const startDate = form.autoInvoicingStartDate.trim();
  if (startDate && addDaysToDateOnly(startDate, 0) === null) {
    errors.autoInvoicingStartDate = 'Enter a date (YYYY-MM-DD)';
  }
  const hourText = form.autoInvoicingNightlyHour.trim();
  const hour = /^\d{1,2}$/.test(hourText) && Number(hourText) <= 23 ? Number(hourText) : null;
  if (hour === null) errors.autoInvoicingNightlyHour = 'Enter an hour from 0 to 23';

  // Dispatch (tracker item 40); the server checks the timezone name against its list
  const timezone = form.timezone.trim();
  if (!timezone) errors.timezone = 'Enter a timezone such as America/New_York';
  const durationText = form.defaultJobDurationMinutes.trim();
  const fallbackDuration = /^\d{1,4}$/.test(durationText) && Number(durationText) >= 5 && Number(durationText) <= 1440 ? Number(durationText) : null;
  if (fallbackDuration === null) errors.defaultJobDurationMinutes = 'Enter a whole number of minutes from 5 to 1440';
  const workDays = workDaysToList(form.workDays);
  if (workDays.length === 0) errors.workDays = 'Pick at least one working day';
  const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;
  if (!timePattern.test(form.workdayStart)) errors.workdayStart = 'Enter a time (HH:MM)';
  if (!timePattern.test(form.workdayEnd)) errors.workdayEnd = 'Enter a time (HH:MM)';
  else if (timePattern.test(form.workdayStart) && form.workdayEnd <= form.workdayStart) errors.workdayEnd = 'Must be after the start time';

  const inboxAccess = form.inboxAccess;
  if (!(types.INBOX_ACCESS_VALUES as readonly string[]).includes(inboxAccess)) errors.inboxAccess = 'Choose who can use the inbox';

  // Routing (tracker item 41)
  const distanceUnit = form.distanceUnit === 'KM' ? 'KM' : form.distanceUnit === 'MI' ? 'MI' : null;
  if (distanceUnit === null) errors.distanceUnit = 'Choose miles or kilometers';
  const speedKph = speedToKph(form.routingSpeed, form.distanceUnit);
  if (speedKph === null || speedKph < MIN_ROUTING_SPEED_KPH || speedKph > MAX_ROUTING_SPEED_KPH) {
    errors.routingSpeed = form.distanceUnit === 'MI' ? 'Enter a speed from 4 to 80 mph' : 'Enter a speed from 5 to 130 km/h';
  }
  const factorText = form.routingCircuityFactor.trim();
  const factor = /^\d(\.\d{1,2})?$/.test(factorText) && Number(factorText) >= 1 && Number(factorText) <= 3 ? Number(factorText).toFixed(2) : null;
  if (factor === null) errors.routingCircuityFactor = 'Enter a number from 1.00 to 3.00';
  const unknownText = form.routingUnknownTravelMinutes.trim();
  const unknownMinutes = /^\d{1,3}$/.test(unknownText) && Number(unknownText) <= 120 ? Number(unknownText) : null;
  if (unknownMinutes === null) errors.routingUnknownTravelMinutes = 'Enter a whole number of minutes from 0 to 120';

  if (Object.keys(errors).length > 0) return { errors };

  return {
    request: {
      ...text,
      companyName: text.companyName!,
      defaultPaymentTermsDays: terms!,
      financeChargeRate: rate!,
      financeChargeGraceDays: graceDays!,
      financeChargeMinAmount: minAmount,
      flatContractBillingMode: mode as types.FlatContractBillingMode,
      autoInvoicingMode: autoMode as types.AutoInvoicingMode,
      // Blank: cleared while OFF; left to the server (today) when turning it on
      ...(startDate ? { autoInvoicingStartDate: startDate } : autoMode === 'OFF' ? { autoInvoicingStartDate: null } : {}),
      autoInvoicingAutoSend: form.autoInvoicingAutoSend === 'true',
      autoInvoicingNightlyHour: hour!,
      timezone,
      defaultJobDurationMinutes: fallbackDuration!,
      workDays,
      workdayStart: form.workdayStart,
      workdayEnd: form.workdayEnd,
      inboxAccess: inboxAccess as types.InboxAccess,
      routingSpeedKph: speedKph!,
      routingCircuityFactor: factor!,
      routingUnknownTravelMinutes: unknownMinutes!,
      distanceUnit: distanceUnit!,
    },
  };
}
